import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HomeLink } from "@/components/app/HomeLink";
import { ReadinessSlider } from "@/components/app/ReadinessSlider";
import { TalkiGuideLine } from "@/components/conversation/TalkiGuideLine";
import { CATALOG_SITUATIONS } from "@/data/catalogScenarios";
import { supabase } from "@/integrations/supabase/client";
import { findCatalogSituation, pickNextRhythm } from "@/lib/catalog";
import { Analytics } from "@/lib/analytics";
import {
  featuredMissionForSituation,
  missionPracticePath,
  sceneRetryCta,
  statusFromConfidence,
  type MissionStatus,
} from "@/lib/missions";
import {
  countUserTurns,
  fallbackAnalysis,
  parseSessionAnalysis,
  type SessionAnalysis,
} from "@/lib/sessionAnalysis";
import { reviewDebrief, shouldInvokeAnalyzer } from "@/lib/sessionResult";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const VERDICT_COPY: Record<MissionStatus, string> = {
  "me-viro": "Me viro",
  quase: "Quase",
  "ainda-nao": "Ainda não",
};

const VERDICT_CLASS: Record<MissionStatus, string> = {
  "me-viro": "bg-blue-50 text-blue-600",
  quase: "bg-orange-50 text-orange-700",
  "ainda-nao": "bg-neutral-100 text-neutral-500",
};

const Review = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<SessionAnalysis | null>(null);
  const [catalogSlug, setCatalogSlug] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confidence, setConfidence] = useState(3);
  const [confidenceTouched, setConfidenceTouched] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!sessionId) {
        setError("Não achei essa prática.");
        setLoading(false);
        return;
      }
      let nextAnalysis: SessionAnalysis | null = null;
      try {
        const { data: session, error: sessionError } = await supabase
          .from("conversation_sessions")
          .select("id, catalog_slug, analysis, status, transcript")
          .eq("id", sessionId)
          .maybeSingle();

        if (sessionError) throw sessionError;
        if (!session) {
          setError("Não achei essa prática.");
          return;
        }

        setCatalogSlug(session.catalog_slug ?? undefined);
        const existing = parseSessionAnalysis(session.analysis);
        const situation = findCatalogSituation(CATALOG_SITUATIONS, session.catalog_slug ?? undefined);
        const transcript = Array.isArray(session.transcript) ? session.transcript : [];
        const userTurns = countUserTurns(transcript);
        const localFallback = fallbackAnalysis({
          userTurns: session.status === "too_short" ? 0 : userTurns || 2,
          situationTitle: situation?.title,
          nextSlug: situation ? pickNextRhythm(CATALOG_SITUATIONS, situation) : session.catalog_slug ?? undefined,
          handled: existing?.handled,
          stuck: existing?.stuck,
          coachLine: existing?.coachLine,
          repaired: existing?.repaired,
          uncertain: existing?.uncertain,
        });
        nextAnalysis = existing;

        if (existing && !cancelled) {
          setAnalysis(existing);
          if (existing.confidenceScore) setConfidence(existing.confidenceScore);
        }

        if (!shouldInvokeAnalyzer(session.status) && existing) {
          return;
        }

        const { data, error: fnError } = await supabase.functions.invoke("analyze-conversation", {
          body: { sessionId },
        });

        const parsed = parseSessionAnalysis(data?.analysis);
        nextAnalysis = parsed ?? existing ?? localFallback;
        if (!cancelled) {
          setAnalysis(nextAnalysis);
          if (nextAnalysis.confidenceScore) setConfidence(nextAnalysis.confidenceScore);
        }
        if (fnError && !parsed) {
          console.error(fnError);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled && !nextAnalysis) {
          setError("Não consegui gerar o feedback agora.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !analysis || !confidenceTouched) return;
    const timer = window.setTimeout(() => {
      void (async () => {
        const { data } = await supabase
          .from("conversation_sessions")
          .select("analysis")
          .eq("id", sessionId)
          .maybeSingle();
        const current = data?.analysis && typeof data.analysis === "object"
          ? data.analysis as Record<string, unknown>
          : {};
        const { error: updateError } = await supabase
          .from("conversation_sessions")
          .update({
            analysis: { ...current, confidenceScore: confidence, confidence_score: confidence },
          })
          .eq("id", sessionId);
        if (updateError) console.error(updateError);
      })();
    }, 400);
    return () => window.clearTimeout(timer);
  }, [confidence, confidenceTouched, sessionId, analysis]);

  const situation = findCatalogSituation(CATALOG_SITUATIONS, catalogSlug);
  const debrief = analysis ? reviewDebrief(situation, analysis) : null;
  const retrySlug = catalogSlug;
  const nextRhythm = situation ? pickNextRhythm(CATALOG_SITUATIONS, situation) : undefined;
  const mission = situation ? featuredMissionForSituation(situation) : undefined;
  const sameMissionHref = nextRhythm
    ? `/app/practice/${nextRhythm}`
    : mission
      ? missionPracticePath(mission.id)
      : "/app/practice";

  const coachLine = useMemo(() => debrief?.coachLine ?? "Vamos repetir o ponto difícil.", [debrief]);
  const sceneTitle = situation?.title ?? "Sua prática";
  const retryHref = retrySlug ? `/app/practice/${retrySlug}` : "/app";

  useEffect(() => {
    if (debrief && !debrief.trusted && !analysis?.tooShort) {
      Analytics.reviewUncertain(catalogSlug);
    }
  }, [analysis?.tooShort, catalogSlug, debrief]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 px-5 py-8">
        <div className="mx-auto max-w-lg space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-56 w-full rounded-[20px]" />
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-50 px-6">
        <p className="text-neutral-900">{error || "Não achei o resultado desta prática."}</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate("/app")}>
          Voltar ao início
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 px-5 pb-10 pt-4 text-neutral-900 md:px-8">
      <header className="mx-auto max-w-lg pt-1">
        <HomeLink />
        <p className="mt-5 text-center text-overline text-neutral-500">
          Como foi
        </p>
        <p className="mt-1 text-center text-[15px] font-semibold text-neutral-900">{sceneTitle}</p>
      </header>

      {analysis.tooShort ? (
        <main className="mx-auto mt-16 max-w-lg text-center">
          <p className="text-lg font-semibold leading-snug">{analysis.summary}</p>
          <div className="mt-6">
            <TalkiGuideLine text="Ensaia de novo quando puder falar um pouco mais." />
          </div>
          <Button asChild className="mt-8 w-full">
            <Link to={retryHref}>Treinar de novo</Link>
          </Button>
          <Button asChild variant="ghost" className="mt-4 w-full">
            <Link to={sameMissionHref}>Outra cena relacionada</Link>
          </Button>
          <Button asChild variant="ghost" className="mt-1 w-full">
            <Link to="/app">Voltar ao início</Link>
          </Button>
        </main>
      ) : (
        <main className="mx-auto mt-8 max-w-lg">
          <div className="flex justify-center">
            <span className={`rounded-full px-4 py-1.5 text-sm font-bold ${VERDICT_CLASS[statusFromConfidence(confidence)]}`}>
              {VERDICT_COPY[statusFromConfidence(confidence)]}
            </span>
          </div>
          <div className="mt-5">
            <ReadinessSlider
              variant="hero"
              value={confidence}
              onChange={(value) => {
                setConfidenceTouched(true);
                setConfidence(value);
              }}
            />
          </div>

          {debrief?.trusted ? (
            <div className="mt-9 flex flex-col gap-2.5">
              <div className="flex gap-2.5">
                <div className="flex-1 rounded-2xl bg-blue-50 px-3.5 py-3.5">
                  <p className="text-xs font-semibold text-blue-600">Se virou em</p>
                  <p className="mt-1.5 text-[15px] font-semibold leading-snug">{debrief.handled}</p>
                </div>
                <div className="flex-1 rounded-2xl bg-orange-50 px-3.5 py-3.5">
                  <p className="text-caption font-semibold text-orange-700">Travou em</p>
                  <p className="mt-1.5 text-[15px] font-semibold leading-snug">{debrief.stuck}</p>
                </div>
              </div>
              {debrief.repaired && (
                <div className="rounded-2xl bg-white px-3.5 py-3.5 ring-1 ring-neutral-200">
                  <p className="text-xs font-semibold text-neutral-500">Se recuperou</p>
                  <p className="mt-1.5 text-[15px] font-semibold leading-snug">{debrief.repaired}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-9 rounded-2xl bg-neutral-50 px-3.5 py-3.5 ring-1 ring-neutral-200">
              <p className="text-xs font-semibold text-neutral-500">Ponto de prática</p>
              <p className="mt-1.5 text-[15px] font-semibold leading-snug">
                O Talki não conseguiu identificar com segurança o ponto de prática.
              </p>
            </div>
          )}

          <div className="mt-6">
            <TalkiGuideLine text={coachLine} />
          </div>

          <Button asChild className="mt-6 w-full">
            <Link to={retryHref}>{sceneRetryCta(debrief?.stuck ?? "", debrief?.trusted ?? false)}</Link>
          </Button>
          <Button asChild variant="ghost" className="mt-4 w-full">
            <Link to={sameMissionHref}>Outra cena relacionada</Link>
          </Button>
          <Button asChild variant="ghost" className="mt-1 w-full">
            <Link to="/app">Voltar ao início</Link>
          </Button>
        </main>
      )}
    </div>
  );
};

export default Review;
